import { createSelector } from "@reduxjs/toolkit";

import { currentDeploymentSelector } from "Reducers/environment/deployment";

import { EMPTY_ENVIRONMENT_TASKS_ENTRY } from "./constants";
import { getEnvironmentTasksKey } from "./utils";

import type {
  EnvironmentTasksParams,
  TaskContainerRow,
  TaskStatus
} from "./types";
import type { Activity } from "Api/activity/types";
import type { RootState } from "Store/configureStore";

const selectEnvironmentTasksState = (state: RootState) =>
  state.environmentTasks;

const selectParams = (_: RootState, params: EnvironmentTasksParams) => params;

const selectTaskName = (_: RootState, params: { taskName: string }) =>
  params.taskName;

/** The per-environment entry, or a stable empty default when not yet loaded. */
export const selectEnvironmentTasksEntry = createSelector(
  selectEnvironmentTasksState,
  selectParams,
  (state, params) =>
    state.byEnvironment[getEnvironmentTasksKey(params)] ??
    EMPTY_ENVIRONMENT_TASKS_ENTRY
);

export const selectEnvironmentTaskDefinitions = createSelector(
  selectEnvironmentTasksEntry,
  entry => entry.definitions
);

export const selectEnvironmentTaskActivities = createSelector(
  selectEnvironmentTasksEntry,
  entry => entry.activities
);

export const selectEnvironmentTasksIsLoading = createSelector(
  selectEnvironmentTasksEntry,
  entry => entry.isLoading
);

export const selectEnvironmentTasksHasLoaded = createSelector(
  selectEnvironmentTasksEntry,
  entry => entry.hasLoaded
);

export const selectEnvironmentTasksError = createSelector(
  selectEnvironmentTasksEntry,
  entry => entry.error
);

/** A single definition by task name (or id). Used by the run action. */
export const selectEnvironmentTaskByName = createSelector(
  selectEnvironmentTaskDefinitions,
  selectTaskName,
  (definitions, taskName) =>
    definitions.find(def => def.name === taskName || def.id === taskName)
);

/** Whether a run action is in flight for a given task. */
export const selectIsTaskRunning = createSelector(
  selectEnvironmentTasksEntry,
  selectTaskName,
  (entry, taskName) => Boolean(entry.runningByTask[taskName])
);

/** The in-flight run map for the environment, keyed by task name. */
export const selectTaskContainersRunningByTask = createSelector(
  selectEnvironmentTasksEntry,
  entry => entry.runningByTask
);

const activityTime = (activity: Activity) =>
  new Date(activity.completed_at || activity.created_at || 0).getTime();

const parseRuntime = (type: string) => {
  // A bare runtime ("nodejs", no colon) yields only `runtime`; the defaults
  // cover that real shape — this is not a guard against a missing `type`.
  const [runtime = "", version = ""] = type.split(":");
  return { runtime, version };
};

const parseCpu = (profileSize: string | null): number | null => {
  if (!profileSize) return null;
  const cpu = parseFloat(profileSize);
  return Number.isNaN(cpu) ? null : cpu;
};

/**
 * The fully-joined table rows: definitions (`/tasks`) ⋈ status + last-run
 * (`environment.task` activities, grouped by `parameters.task`) ⋈ allocation
 * size (deployment topology). Sorted by name; the table applies interactive sort.
 *
 * Allocation degrades gracefully: CPU + size whenever the topology has a size,
 * RAM + profile once the topology carries the resolved `container_profile`
 * (GIT-1240) — null on APIs predating it.
 */
export const selectTaskContainerRows = createSelector(
  selectEnvironmentTaskDefinitions,
  selectEnvironmentTaskActivities,
  currentDeploymentSelector,
  (definitions, activities, deployment): TaskContainerRow[] => {
    const latestByTask = new Map<string, Activity>();
    // Concurrent runs per task. Each run is its own activity that transitions
    // in place, so its `state` is the source of truth: a run is live exactly
    // while its activity is `in_progress`. A run that finishes never suppresses
    // another still going — comparing one run's start against another's finish
    // read a still-running task as idle once one of its runs completed (CON-1461).
    const runningCountByTask = new Map<string, number>();

    for (const activity of activities) {
      const taskName = activity.parameters?.task;
      if (typeof taskName !== "string") continue;

      const time = activityTime(activity);

      const current = latestByTask.get(taskName);
      if (!current || time > activityTime(current)) {
        latestByTask.set(taskName, activity);
      }

      if (activity.state === "in_progress") {
        runningCountByTask.set(
          taskName,
          (runningCountByTask.get(taskName) ?? 0) + 1
        );
      }
    }

    const containerProfiles = deployment?.container_profiles;
    const topologyTasks = deployment?.tasks;

    // Resolve each relationship target (`{ service, endpoint }`) to the target
    // service's type, which drives its icon. Built once from the topology.
    const serviceTypeByName: Record<string, string> = {};
    for (const group of [
      deployment?.services,
      deployment?.webapps,
      deployment?.workers
    ]) {
      for (const [serviceName, service] of Object.entries(group ?? {})) {
        if (service?.type) serviceTypeByName[serviceName] = service.type;
      }
    }

    return definitions
      .map((def): TaskContainerRow => {
        const { runtime, version } = parseRuntime(def.type);

        // Status is Running while any run is in progress; the count is how many
        // are concurrently live. `latest` (most recent by time) still feeds the
        // last-run timestamp + result — when a run is live the UI shows "now",
        // so a run that just finished alongside a live one never reads as idle.
        const latest = latestByTask.get(def.name);
        const runningCount = runningCountByTask.get(def.name) ?? 0;
        const status: TaskStatus = runningCount > 0 ? "running" : "idle";

        // Both come from the deployment topology sizing entry. `profile` is the
        // RESOLVED container profile (GIT-1240); the `/tasks` definition only
        // carries the raw config value, so it is not used here. RAM stays null
        // until an API with GIT-1240 populates `container_profile`.
        const topologyTask = topologyTasks?.[def.name];
        const profileSize = topologyTask?.resources?.profile_size ?? null;
        const profile = topologyTask?.container_profile ?? null;
        const memory =
          profile && profileSize
            ? (containerProfiles?.[profile]?.[profileSize]?.memory ?? null)
            : null;

        return {
          id: def.id,
          name: def.name,
          type: def.type,
          runtime,
          version,
          command: def.run.command,
          relationships: Object.entries(def.relationships).map(
            ([alias, target]) => ({
              alias,
              service: target.service,
              serviceType: serviceTypeByName[target.service] ?? null
            })
          ),
          status,
          runningCount,
          lastRun: {
            at: latest
              ? latest.completed_at || latest.created_at || null
              : null,
            result: latest?.result ?? null,
            state: latest?.state ?? null
          },
          allocation: {
            profileSize,
            cpu: parseCpu(profileSize),
            memory,
            profile
          },
          // `#run` is optional on the type — a task isn't always runnable.
          runHref: def._links["#run"]?.href ?? null
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }
);

/**
 * Total in-flight runs across all tasks — the header's "N Running" tag. Sums
 * concurrent runs, so one task running twice reads as 2 (not 1 running task).
 */
export const selectTaskContainersRunningCount = createSelector(
  selectTaskContainerRows,
  rows => rows.reduce((total, row) => total + row.runningCount, 0)
);

/**
 * How many task ROWS are running — one per task, however many concurrent runs
 * it has. `selectTaskContainersRunningCount` counts the runs instead, so the two
 * diverge whenever a task runs twice at once. This is the row-count one: it
 * drives how far the card stretches its list to keep running tasks on screen.
 */
export const selectTaskContainersRunningRowCount = createSelector(
  selectTaskContainerRows,
  rows => rows.filter(row => row.status === "running").length
);

/** Each task's running/idle status, keyed by task name. */
export const selectTaskStatusByName = createSelector(
  selectTaskContainerRows,
  rows =>
    Object.fromEntries(rows.map(row => [row.name, row.status])) as Record<
      string,
      TaskStatus
    >
);

export const selectTaskContainersIdleCount = createSelector(
  selectTaskContainerRows,
  rows => rows.filter(row => row.status === "idle").length
);

/** True when the environment has at least one declared task container. */
export const selectHasTaskContainers = createSelector(
  selectTaskContainerRows,
  rows => rows.length > 0
);
